import { DigitalWalletFilterRequest } from "@/models/digital.wallet";
import { Box, Collapse, FormControl, Grid, IconButton, InputAdornment, Link } from "@mui/material";
import { FC, useEffect, useState } from "react";
import InputFilterFieldStyled from "../styled/input.filter.field.styled";
import InputLabelStyled from "../styled/input.label.styled";
import SearchIcon from "@mui/icons-material/Search";
import DigitalWalletStatusSelect from "../common/digital.wallet.status.select";
import BaseTooltip from "../common/baseTooltip";
import BlueButtonStyled from "../styled/blue.button.styled";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import DatePickerComponent from "../common/date.picker.component";
import StatusSelect from "../common/status.select";
import { useTranslation } from "react-i18next";
import { Moment } from "moment";
import filtersStore from "@/stores/filtersStore";

interface IDigitalWalletListFilter {
    searchClick: Function;
    filter: DigitalWalletFilterRequest;
    setFilter: Function;
    collapseFilterClick: Function;
}

const DigitalWalletListFilter: FC<IDigitalWalletListFilter> = ({
    searchClick,
    filter,
    setFilter,
    collapseFilterClick,
}: IDigitalWalletListFilter) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState<boolean>(false);

    const onKeyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            searchClick();
        }
    };

    const setHiddenFilter = () => {
        setOpen(!open);
        filtersStore.setDigitalWalletsHiddenFilter(!open);
        collapseFilterClick();
    };

    const clearFilter = () => {
        filtersStore.setDigitalWalletsHiddenFilter(null);
        setFilter(() => ({
            ...new DigitalWalletFilterRequest(),
        }));
        searchClick();
    };

    function fetchHiddenFilter() {
        if (filtersStore.digitalWalletsHiddenFilter != null) {
            setOpen(filtersStore.digitalWalletsHiddenFilter);
        }
    }

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            fetchHiddenFilter();
        }
        return () => {
            ignore = true;
        };
    }, []);

    return (
        <Grid
            sx={{
                background: "#FFFFFF",
                border: "1px solid rgba(64, 82, 137, 0.25)",
                borderRadius: "12px",
                padding: "15px",
                width: "100%",
                margin: "20px 0",
                marginBottom: "0px",
            }}
        >
            <Grid sx={{ marginBottom: "5px" }}>
                <InputLabelStyled
                    shrink
                    disableAnimation
                    size="normal"
                    htmlFor="searchFilter-freetext"
                    className="free-text-label"
                >
                    {t("general.SearchFilterHeader")}
                </InputLabelStyled>
            </Grid>
            <Grid
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                }}
            >
                <Grid
                    sx={{
                        display: "flex",
                        gap: "15px",
                    }}
                >
                    {" "}
                    <FormControl variant="standard" sx={{ width: "300px" }}>
                        <InputFilterFieldStyled
                            label={t("general.SearchFilterEmployeesName")}
                            type="search"
                            size="small"
                            value={filter.employeeName}
                            onKeyDown={onKeyPressHandler}
                            onChange={(event) =>
                                setFilter(() => ({
                                    ...filter,
                                    employeeName: event.target.value,
                                }))
                            }
                        />
                    </FormControl>
                    <FormControl size="small" sx={{ width: "300px" }}>
                        <DigitalWalletStatusSelect
                            value={filter.walletStatus}
                            title={t("general.SearchFilterWalletStatus")}
                            updateStatus={(args?: string | null) => {
                                setFilter(() => ({
                                    ...filter,
                                    walletStatus: args,
                                }));
                            }}
                        ></DigitalWalletStatusSelect>
                    </FormControl>
                    <FormControl variant="standard" sx={{ width: "300px" }}>
                        <InputFilterFieldStyled
                            label={t("general.SearchFilterEmployeesNumber")}
                            type="search"
                            size="small"
                            value={filter.employeeNumber}
                            onKeyDown={onKeyPressHandler}
                            onChange={(event) =>
                                setFilter(() => ({
                                    ...filter,
                                    employeeNumber: event.target.value,
                                }))
                            }
                        />
                    </FormControl>
                    <FormControl size="small" sx={{ width: "300px" }}>
                        <StatusSelect
                            value={filter.employeeStatus}
                            title={t("general.SearchFilterEmployeesStatus")}
                            updateStatus={(args: boolean | null) =>
                                setFilter(() => ({
                                    ...filter,
                                    employeeStatus: args,
                                }))
                            }
                        ></StatusSelect>
                    </FormControl>
                    <FormControl>
                        <BaseTooltip
                            title={!open ? t("orders.FilterButtonShow") : t("orders.FilterButtonHide")}
                            placement="bottom"
                        >
                            <IconButton
                                className="show-hidden-filter-button-div"
                                onClick={() => setHiddenFilter()}
                                type="button"
                            >
                                {open ? (
                                    <KeyboardDoubleArrowUpIcon className="show-hidden-filter-button" />
                                ) : (
                                    <KeyboardDoubleArrowDownIcon className="show-hidden-filter-button" />
                                )}
                            </IconButton>
                        </BaseTooltip>
                    </FormControl>
                </Grid>
                <Grid
                    sx={{
                        display: "flex",
                        gap: "20px",
                    }}
                >
                    {" "}
                    <FormControl
                        sx={{
                            display: "flex",
                            flexDirection: "column-reverse",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                            }}
                            className="link-element clear-all-btn"
                            onClick={() => {
                                clearFilter();
                            }}
                        >
                            {t("display.ClearAll")}
                        </Box>
                    </FormControl>
                    <FormControl>
                        <BlueButtonStyled onClick={() => searchClick()} sx={{ width: "100%", maxWidth: "110px" }}>
                            {t("general.filter")}
                        </BlueButtonStyled>
                    </FormControl>
                </Grid>
            </Grid>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <Grid
                    sx={{
                        display: "flex",
                        gap: "15px",
                        paddingTop: "20px",
                    }}
                >
                    <FormControl variant="standard" sx={{ width: "20%" }}>
                        <DatePickerComponent
                            title={t("digitalWallet.ExpiryDate")}
                            value={filter.expiredDate}
                            updateDate={(args: Moment | null) =>
                                setFilter(() => ({
                                    ...filter,
                                    expiredDate: args,
                                }))
                            }
                        />
                    </FormControl>
                    <FormControl size="small" sx={{ width: "20%" }}>
                        <InputFilterFieldStyled
                            label={t("general.SearchFilterPhone")}
                            type="search"
                            size="small"
                            value={filter.phone}
                            onKeyDown={onKeyPressHandler}
                            onChange={(event) =>
                                setFilter(() => ({
                                    ...filter,
                                    phone: event.target.value,
                                }))
                            }
                        />
                    </FormControl>

                    <FormControl variant="standard" sx={{ width: "20%" }}>
                        <DatePickerComponent
                            title={t("digitalWallet.LastUseDate")}
                            value={filter.lastUseDate}
                            updateDate={(args: Moment | null) =>
                                setFilter(() => ({
                                    ...filter,
                                    lastUseDate: args,
                                }))
                            }
                        />
                    </FormControl>
                </Grid>
            </Collapse>
        </Grid>
    );
};

export default DigitalWalletListFilter;
